const NOT_STARTED = {
  label: "Not Started",
  classes: "bg-blue-50 text-blue-600 border-blue-100",
  dot: "bg-blue-500",
  completed: false,
};
const IN_PROGRESS = {
  label: "In Progress",
  classes: "bg-orange-50 text-orange-600 border-orange-100",
  dot: "bg-orange-500",
  completed: false,
};
// Two labels for the same finished state. "Completed" is reserved for
// the orientation video, where finishing is a real program milestone
// (it sets orientation_complete in the CRM). Every other video is just
// content a learner has seen, so it reads "Watched" -- plainer, and it
// doesn't imply the watch earned credit (credit is the minutes counter,
// not any single video). Styling is identical for both.
const COMPLETED = {
  label: "Completed",
  classes: "bg-emerald-50 text-emerald-600 border-emerald-100",
  dot: "bg-emerald-500",
  completed: true,
};
const WATCHED = {
  label: "Watched",
  classes: "bg-emerald-50 text-emerald-600 border-emerald-100",
  dot: "bg-emerald-500",
  completed: true,
};

// A non-orientation video counts as completed within this many seconds of
// the end, so stopping during an outro/end-card still reads as done. Keep
// in sync with nothing else -- this is the single source of truth;
// useVideos imports isCompleted below.
export const COMPLETION_BUFFER_SECONDS = 10;

// The orientation video completes at 80% of its length -- the exact
// threshold both CRM writers use (the stream-driven reporting lambda and
// the CCL flow's video-status lambda, WATCH_THRESHOLD = 0.8). Matching it
// means the "Completed" badge turns green at the same instant the CRM
// records the participant as having finished orientation, so the badge is
// a truthful compliance signal. (The end-buffer rule above is looser than
// this on a long video, but orientation is short and its seek bar is
// rewind-only, so 80% is the meaningful line.)
const ORIENTATION_COMPLETION_FRACTION = 0.8;

// Orientation videos are a program's required first watch. There is one per
// benefit program, distinguished by catalog category (SNAP = "Orientation",
// TANF = "TANF Orientation"); both get the compliance rules (80% completion
// here, rewind-only seek bar in VideoModal), matching the backend's
// program-split milestone logic.
const ORIENTATION_CATEGORIES = new Set(["orientation", "tanf orientation"]);

export const isOrientation = (video) =>
  video?.is_intro === true ||
  ORIENTATION_CATEGORIES.has((video?.category || "").toLowerCase().trim());

// Dashboard pinning stays SNAP-only (an explicit is_intro flag or the legacy
// "Orientation" category): the UI has no per-user program context, so pinning
// the TANF video would put it atop every (mostly SNAP) learner's grid. Same
// rule the catalog uses for its intro slot (taxonomy tagVideo). Revisit when
// the portal knows the learner's referral programs.
export const isPinnedOrientation = (video) =>
  video?.is_intro === true ||
  (video?.category || "").toLowerCase().trim() === "orientation";

// Shared completion test. Pass the furthest position reached
// (getFurthestPosition), NOT the resume position, or a rewatch/scrub-back
// would un-complete the video. `saved` and `videoLength` may arrive as
// strings from the API or as `undefined` for a row created before its
// YouTube metadata resolved (see PR-D handleSave); a zero or non-finite
// videoLength would otherwise let `saved >= -10` mark every row as done.
export const isCompleted = (savedTime, video) => {
  const saved = Number(savedTime);
  const length = Number(video?.videoLength);
  const hasValidLength = Number.isFinite(length) && length > 0;
  if (!hasValidLength || !Number.isFinite(saved)) return false;
  const completeAt = isOrientation(video)
    ? length * ORIENTATION_COMPLETION_FRACTION
    : length - COMPLETION_BUFFER_SECONDS;
  return saved >= completeAt;
};

// Returns the progress badge for a video given the furthest position the
// learner has reached (pass getFurthestPosition(entry), not the raw resume
// position, or the badge regresses on rewatch).
const getProgressData = (savedTime, video) => {
  const saved = Number(savedTime);
  const hasProgress = Number.isFinite(saved) && saved > 0;

  if (!hasProgress) return NOT_STARTED;
  if (isCompleted(savedTime, video)) {
    return isOrientation(video) ? COMPLETED : WATCHED;
  }
  return IN_PROGRESS;
};

export default getProgressData;
